import React, { useState, useEffect } from "react";
import { ZonaHoraria } from "@/components/ZonaHoraria";
import { AppCorreo } from "@/components/AppCorreo";
import { PushPrefs } from "@/components/push/PushPrefs";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { EnviarComentario } from "@/components/EnviarComentario";
import { ComentariosBeta } from "@/components/ComentariosBeta";
import { PanelActividad } from "@/components/PanelActividad";
import { AccesosBeta } from "@/components/AccesosBeta";
import { PrivacidadSeguridad } from "@/components/PrivacidadSeguridad";
import { Field, Coinciden } from "@/pages/Login";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

export default function Perfil() {
  const navigate = useNavigate();
  const { user, logout, updateNombre, eliminarCuenta } = useAuth();
  const [nombre, setNombre] = useState(user?.nombre || "");
  const [busy, setBusy] = useState(false);
  const [pwd, setPwd] = useState({ actual: "", nueva: "", repite: "" });
  const [pwdBusy, setPwdBusy] = useState(false);
  const [verifSent, setVerifSent] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [delConfirm, setDelConfirm] = useState("");
  const [delPwd, setDelPwd] = useState("");
  const [delBusy, setDelBusy] = useState(false);

  const save = async () => {
    if (!nombre.trim()) return;
    setBusy(true);
    try {
      await updateNombre(nombre.trim());
      toast.success("Perfil actualizado");
    } catch {
      toast.error("No se pudo guardar el perfil.");
    } finally {
      setBusy(false);
    }
  };

  const cambiarPassword = async () => {
    if (pwd.nueva.length < 8) return toast.error("La nueva contraseña debe tener al menos 8 caracteres.");
    if (pwd.nueva !== pwd.repite) return toast.error("Las contraseñas no coinciden.");
    setPwdBusy(true);
    try {
      await api.post("/auth/cambiar-password", { actual: pwd.actual, nueva: pwd.nueva });
      toast.success("Contraseña actualizada");
      setPwd({ actual: "", nueva: "", repite: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo cambiar la contraseña.");
    } finally {
      setPwdBusy(false);
    }
  };

  const reenviarVerificacion = async () => {
    try {
      await api.post("/auth/reenviar-verificacion");
      setVerifSent(true);
      toast.success("Correo de verificación enviado", { description: "Revisa tu bandeja de entrada." });
    } catch {
      toast.error("No se pudo enviar el correo de verificación.");
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const emailOk = delConfirm.trim().toLowerCase() === (user?.email || "").toLowerCase();
  const pwdNeeded = user?.proveedor === "email";
  const canDelete = emailOk && (!pwdNeeded || delPwd.length > 0) && !delBusy;

  const doDelete = async () => {
    setDelBusy(true);
    try {
      await eliminarCuenta(delPwd);
      toast.success("Cuenta eliminada definitivamente");
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo eliminar la cuenta.");
    } finally {
      setDelBusy(false);
    }
  };

  const proveedorLabel = user?.proveedor === "google" ? "Google" : "Correo y contraseña";

  return (
    <div className="space-y-5">
      <header className="aldia-fade-up">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#1C2421]">Tu perfil</h1>
        <p className="text-base text-[#6E7A74] mt-1">Tus datos y preferencias de cuenta.</p>
      </header>

      <div className="bg-white border border-[#E5DFD5] rounded-2xl p-6 shadow-sm flex items-center gap-4">
        {user?.foto ? (
          <img src={user.foto} alt="" className="w-16 h-16 rounded-full object-cover" />
        ) : (
          <div className="w-16 h-16 rounded-full bg-[#3A6B56] flex items-center justify-center font-heading font-bold text-2xl text-white">
            {(user?.nombre || "?").charAt(0)}
          </div>
        )}
        <div className="min-w-0">
          <p className="font-heading text-xl font-bold text-[#1C2421] truncate">{user?.nombre}</p>
          <p className="text-base text-[#6E7A74] truncate">{user?.email}</p>
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#2E5645] bg-[#E8F0EC] rounded-full px-2.5 py-1 mt-1.5">
            <Icons.ShieldCheck size={13} /> {proveedorLabel}
          </span>
        </div>
      </div>

      {/* Verificación de email */}
      {user?.proveedor === "email" && !user?.email_verificado && (
        <div className="bg-[#FFFBEB] border border-[#FADFA0] rounded-2xl p-4 flex items-start gap-3" data-testid="perfil-verif-banner">
          <Icons.MailWarning size={20} className="text-[#D97706] shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-base font-semibold text-[#92400E]">Tu correo aún no está verificado</p>
            <p className="text-sm text-[#92400E]/80 mt-0.5">Verifícalo para proteger tu cuenta.</p>
            <button
              data-testid="perfil-reenviar-verif"
              onClick={reenviarVerificacion}
              disabled={verifSent}
              className="mt-2 text-sm font-bold text-[#3A6B56] disabled:opacity-60"
            >
              {verifSent ? "Correo enviado" : "Reenviar correo de verificación"}
            </button>
          </div>
        </div>
      )}
      {user?.email_verificado && (
        <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-xl p-3 flex items-center gap-2" data-testid="perfil-verif-ok">
          <Icons.BadgeCheck size={18} className="text-[#16A34A]" />
          <p className="text-sm font-semibold text-[#15803D]">Correo verificado</p>
        </div>
      )}

      <div className="bg-white border border-[#E5DFD5] rounded-2xl p-6 shadow-sm space-y-4">
        <div>
          <label className="block text-sm font-semibold text-[#4A5550] mb-1.5">Nombre</label>
          <input
            data-testid="perfil-nombre"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            className="w-full min-h-[56px] px-4 rounded-xl bg-[#FAF8F5] border border-[#E5DFD5] text-base text-[#1C2421] focus:outline-none focus:ring-4 focus:ring-[#3A6B56]/20 focus:border-[#3A6B56]"
          />
        </div>
        <button
          data-testid="perfil-guardar"
          onClick={save}
          disabled={busy}
          className="w-full min-h-[56px] rounded-xl bg-[#3A6B56] text-white font-semibold text-lg hover:bg-[#2E5645] transition-colors disabled:opacity-60"
        >
          {busy ? "Guardando…" : "Guardar cambios"}
        </button>
      </div>

      {/* Cambiar contraseña */}
      <div className="bg-white border border-[#E5DFD5] rounded-2xl p-6 shadow-sm space-y-4">
        <h2 className="font-heading text-lg font-bold text-[#1C2421]">Cambiar contraseña</h2>
        {user?.proveedor === "email" && (
          <PwdInput label="Contraseña actual" value={pwd.actual} onChange={(v) => setPwd({ ...pwd, actual: v })} testid="perfil-pwd-actual" autoComplete="current-password" />
        )}
        <PwdInput label="Nueva contraseña (mínimo 8)" value={pwd.nueva} onChange={(v) => setPwd({ ...pwd, nueva: v })} testid="perfil-pwd-nueva" autoComplete="new-password" />
        <PwdInput label="Repite la nueva contraseña" value={pwd.repite} onChange={(v) => setPwd({ ...pwd, repite: v })} testid="perfil-pwd-repite" autoComplete="new-password" invalido={!!pwd.repite && pwd.repite !== pwd.nueva} />
        <Coinciden a={pwd.nueva} b={pwd.repite} testid="perfil-pwd-coinciden" />
        <button
          data-testid="perfil-pwd-guardar"
          onClick={cambiarPassword}
          disabled={pwdBusy || (!!pwd.repite && pwd.repite !== pwd.nueva)}
          className="w-full min-h-[56px] rounded-xl bg-[#F2EDE4] text-[#1C2421] font-semibold text-base border border-[#E5DFD5] hover:bg-[#E5DFD5] transition-colors disabled:opacity-60"
        >
          {pwdBusy ? "Guardando…" : "Actualizar contraseña"}
        </button>
      </div>

      <div className="flex items-start gap-2 bg-[#F6F3EE] rounded-xl p-4">
        <Icons.Lock size={18} className="text-[#3A6B56] shrink-0 mt-0.5" />
        <p className="text-sm text-[#6E7A74] leading-relaxed">
          Tus datos son privados y solo tú puedes verlos. Los documentos nunca se comparten
          mediante enlaces públicos.
        </p>
      </div>

      <AvisoPrefs />

      <PushPrefs />
      <ZonaHoraria />
      <AppCorreo />
      <button data-testid="perfil-ver-introduccion" onClick={() => window.dispatchEvent(new Event("aldia-intro"))}
        className="w-full min-h-[56px] bg-white border border-[#E5DFD5] rounded-2xl px-5 flex items-center gap-3 text-left shadow-sm hover:bg-[#FAF8F5]">
        <span className="w-10 h-10 rounded-xl bg-[#E8F0EC] flex items-center justify-center"><Icons.Sparkles size={20} className="text-[#3A6B56]" /></span>
        <span className="flex-1 font-semibold text-base text-[#1C2421]">Ver introducción de nuevo</span>
        <Icons.ChevronRight size={20} className="text-[#C4BDB0]" />
      </button>

      <EnviarComentario />
      {user?.es_admin && <ComentariosBeta />}
      {user?.es_admin && <PanelActividad />}
      {user?.es_admin && <AccesosBeta />}

      <ResumenSemanalPref />

      <div className="grid grid-cols-1 gap-3">
        <button
          data-testid="perfil-link-documentos"
          onClick={() => navigate("/documentos")}
          className="w-full bg-white border border-[#E5DFD5] rounded-2xl p-5 shadow-sm flex items-center gap-3 hover:border-[#3A6B56] transition-colors text-left"
        >
          <div className="shrink-0 w-11 h-11 rounded-xl bg-[#E8F0EC] flex items-center justify-center">
            <Icons.FileText size={22} className="text-[#3A6B56]" />
          </div>
          <div className="flex-1">
            <p className="font-heading font-bold text-base text-[#1C2421]">Documentos</p>
            <p className="text-sm text-[#6E7A74]">Facturas, pólizas y archivos guardados.</p>
          </div>
          <Icons.ChevronRight size={20} className="text-[#C4BDB0]" />
        </button>
        <button
          data-testid="perfil-link-correos"
          onClick={() => navigate("/correos")}
          className="w-full bg-white border border-[#E5DFD5] rounded-2xl p-5 shadow-sm flex items-center gap-3 hover:border-[#3A6B56] transition-colors text-left"
        >
          <div className="shrink-0 w-11 h-11 rounded-xl bg-[#E8F0EC] flex items-center justify-center">
            <Icons.Mail size={22} className="text-[#3A6B56]" />
          </div>
          <div className="flex-1">
            <p className="font-heading font-bold text-base text-[#1C2421]">Historial de correos</p>
            <p className="text-sm text-[#6E7A74]">Correos preparados y su estado.</p>
          </div>
          <Icons.ChevronRight size={20} className="text-[#C4BDB0]" />
        </button>
        <button
          data-testid="perfil-link-contactos"
          onClick={() => navigate("/contactos")}
          className="w-full bg-white border border-[#E5DFD5] rounded-2xl p-5 shadow-sm flex items-center gap-3 hover:border-[#3A6B56] transition-colors text-left"
        >
          <div className="shrink-0 w-11 h-11 rounded-xl bg-[#E8F0EC] flex items-center justify-center">
            <Icons.Users size={22} className="text-[#3A6B56]" />
          </div>
          <div className="flex-1">
            <p className="font-heading font-bold text-base text-[#1C2421]">Agenda de contactos</p>
            <p className="text-sm text-[#6E7A74]">Guarda destinatarios frecuentes.</p>
          </div>
          <Icons.ChevronRight size={20} className="text-[#C4BDB0]" />
        </button>
      </div>

      <AlertDialog>
        <AlertDialogTrigger asChild>
          <button
            data-testid="perfil-logout"
            className="w-full min-h-[56px] rounded-xl bg-white border-2 border-[#FECACA] text-[#B91C1C] font-semibold text-lg hover:bg-[#FEF2F2] transition-colors flex items-center justify-center gap-2"
          >
            <Icons.LogOut size={22} /> Cerrar sesión
          </button>
        </AlertDialogTrigger>
        <AlertDialogContent data-testid="logout-confirm" className="bg-[#FAF8F5] border-[#E5DFD5]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-xl text-[#1C2421]">¿Cerrar sesión?</AlertDialogTitle>
            <AlertDialogDescription className="text-base text-[#6E7A74]">
              Tendrás que volver a iniciar sesión para acceder a tus asuntos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel data-testid="logout-cancel" className="min-h-[52px] text-base">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="logout-confirm-btn"
              onClick={doLogout}
              className="min-h-[52px] text-base bg-[#B91C1C] hover:bg-[#991B1B] text-white"
            >
              Cerrar sesión
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PrivacidadSeguridad />

      {/* Zona de peligro: eliminar cuenta */}
      <div className="bg-white border-2 border-[#FECACA] rounded-2xl p-6 shadow-sm space-y-3">
        <h2 className="font-heading text-lg font-bold text-[#B91C1C] flex items-center gap-2">
          <Icons.AlertTriangle size={20} /> Eliminar cuenta
        </h2>
        <p className="text-sm text-[#6E7A74] leading-relaxed">
          Se borrarán de tu cuenta tus personas, mascotas, viviendas, vehículos, asuntos, documentos,
          gastos, gestiones, planes de ocio, conversaciones con el Agente y preferencias de avisos.
          Los archivos que subiste dejarán de estar accesibles desde AL DÍA. Esta acción no se puede deshacer.
        </p>
        <AlertDialog
          open={delOpen}
          onOpenChange={(o) => { setDelOpen(o); if (!o) { setDelConfirm(""); setDelPwd(""); } }}
        >
          <AlertDialogTrigger asChild>
            <button
              data-testid="perfil-eliminar-cuenta"
              className="w-full min-h-[52px] rounded-xl bg-[#FEF2F2] border border-[#FECACA] text-[#B91C1C] font-semibold text-base hover:bg-[#FEE2E2] transition-colors flex items-center justify-center gap-2"
            >
              <Icons.Trash2 size={20} /> Eliminar mi cuenta
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid="eliminar-cuenta-dialog" className="bg-[#FAF8F5] border-[#E5DFD5]">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading text-xl text-[#B91C1C]">¿Eliminar tu cuenta para siempre?</AlertDialogTitle>
              <AlertDialogDescription className="text-base text-[#6E7A74]">
                Esta acción es permanente. Para confirmar, escribe tu correo{pwdNeeded ? " e introduce tu contraseña" : ""}.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-3 py-1">
              <div>
                <label className="block text-sm font-semibold text-[#4A5550] mb-1.5">Escribe tu correo: <span className="text-[#1C2421]">{user?.email}</span></label>
                <input
                  data-testid="eliminar-cuenta-email"
                  value={delConfirm}
                  onChange={(e) => setDelConfirm(e.target.value)}
                  placeholder={user?.email}
                  className="w-full min-h-[52px] px-4 rounded-xl bg-white border border-[#E5DFD5] text-base text-[#1C2421] placeholder-[#9AA39D] focus:outline-none focus:ring-4 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C]"
                />
              </div>
              {pwdNeeded && (
                <Field label="Tu contraseña" type="password" value={delPwd} onChange={setDelPwd} testid="eliminar-cuenta-password" autoComplete="current-password" />
              )}
            </div>
            <AlertDialogFooter className="gap-2">
              <AlertDialogCancel data-testid="eliminar-cuenta-cancel" className="min-h-[52px] text-base">Cancelar</AlertDialogCancel>
              <AlertDialogAction
                data-testid="eliminar-cuenta-confirm-btn"
                disabled={!canDelete}
                onClick={(e) => { e.preventDefault(); doDelete(); }}
                className="min-h-[52px] text-base bg-[#B91C1C] hover:bg-[#991B1B] text-white disabled:opacity-50"
              >
                {delBusy ? "Eliminando…" : "Eliminar definitivamente"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

const PwdInput = ({ label, value, onChange, testid, autoComplete, invalido }) => (
  <Field label={label} type="password" value={value} onChange={onChange} testid={testid} autoComplete={autoComplete} invalido={invalido} />
);

const AVISO_TIPOS = [
  { id: "vencimiento", label: "Vencimientos próximos" },
  { id: "atencion", label: "Asuntos que requieren atención" },
  { id: "familiar", label: "Asuntos de familiares" },
];

const HORAS_RESUMEN = Array.from({ length: 17 }, (_, i) => i + 6); // 06:00 … 22:00
const fmtHora = (h) => `${String(h).padStart(2, "0")}:00`;

const ResumenSemanalPref = () => {
  const { getResumenPref, setResumenPref, getResumenPreview } = useData();
  const [activo, setActivo] = useState(null);
  const [hora, setHora] = useState(8);
  const [savingHora, setSavingHora] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState(null); // {vacio, html, asunto} | "loading"
  useEffect(() => {
    getResumenPref()
      .then((d) => { setActivo(d.activo); setHora(d.hora ?? 8); })
      .catch(() => { setActivo(true); setHora(8); });
    // eslint-disable-next-line
  }, []);
  const toggle = async () => {
    const nuevo = !activo;
    setActivo(nuevo);
    try {
      await setResumenPref({ activo: nuevo });
      toast.success(nuevo ? "Resumen semanal activado" : "Resumen semanal desactivado");
    } catch {
      setActivo(!nuevo);
      toast.error("No se pudo actualizar.");
    }
  };
  const cambiarHora = async (e) => {
    const nueva = parseInt(e.target.value, 10);
    const anterior = hora;
    setHora(nueva);
    setSavingHora(true);
    try {
      await setResumenPref({ hora: nueva });
      toast.success(`Se enviará los lunes a las ${fmtHora(nueva)}`);
    } catch {
      setHora(anterior);
      toast.error("No se pudo guardar la hora.");
    } finally {
      setSavingHora(false);
    }
  };
  const verPreview = async () => {
    setPreviewOpen(true);
    setPreview("loading");
    try {
      const d = await getResumenPreview();
      setPreview(d);
    } catch {
      setPreview({ error: true });
    }
  };
  if (activo === null) return null;
  return (
    <div className="bg-white border border-[#E5DFD5] rounded-2xl p-6 shadow-sm" data-testid="resumen-semanal-prefs">
      <h2 className="font-heading text-lg font-bold text-[#1C2421] flex items-center gap-2">
        <Icons.CalendarCheck size={20} className="text-[#3A6B56]" /> Resumen semanal
      </h2>
      <p className="text-sm text-[#6E7A74] mt-1 mb-4">
        Cada lunes te enviamos un correo breve con lo relevante de la semana:
        vencimientos, citas, asuntos que requieren atención y recordatorios. Si no hay nada
        pendiente, no se envía ningún correo.
      </p>
      <button
        data-testid="resumen-semanal-toggle"
        onClick={toggle}
        className="w-full flex items-center justify-between gap-3 min-h-[52px] px-4 rounded-xl bg-[#FAF8F5] border border-[#E5DFD5] hover:border-[#3A6B56] transition-colors"
      >
        <span className="text-base text-[#1C2421]">Resumen semanal por email</span>
        <span className={`relative w-12 h-7 rounded-full transition-colors shrink-0 ${activo ? "bg-[#3A6B56]" : "bg-[#D6CFC2]"}`}>
          <span className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-all ${activo ? "left-6" : "left-1"}`} />
        </span>
      </button>

      <div className={`mt-3 flex items-center justify-between gap-3 min-h-[52px] px-4 rounded-xl bg-[#FAF8F5] border border-[#E5DFD5] transition-opacity ${activo ? "" : "opacity-50 pointer-events-none"}`}>
        <div className="flex items-center gap-2">
          <Icons.Clock size={18} className="text-[#3A6B56]" />
          <span className="text-base text-[#1C2421]">Hora de envío (lunes)</span>
        </div>
        <select
          data-testid="resumen-semanal-hora"
          value={hora}
          onChange={cambiarHora}
          disabled={!activo || savingHora}
          className="h-11 px-3 rounded-lg bg-white border border-[#E5DFD5] text-base text-[#1C2421] font-medium focus:border-[#3A6B56] focus:outline-none"
        >
          {HORAS_RESUMEN.map((h) => (
            <option key={h} value={h}>{fmtHora(h)}</option>
          ))}
        </select>
      </div>
      <p className="text-xs text-[#6E7A74] mt-2">
        Hora peninsular (Europe/Madrid). Actualmente: <strong>lunes a las {fmtHora(hora)}</strong>.
      </p>

      <button
        data-testid="resumen-semanal-preview-btn"
        onClick={verPreview}
        className="mt-4 w-full min-h-[52px] rounded-xl bg-white border-2 border-[#3A6B56] text-[#3A6B56] font-semibold text-base hover:bg-[#F2F6F4] transition-colors flex items-center justify-center gap-2"
      >
        <Icons.Eye size={20} /> Ver vista previa
      </button>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="bg-[#FAF8F5] border-[#E5DFD5] max-w-lg" data-testid="resumen-semanal-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#1C2421]">Vista previa del resumen</DialogTitle>
            <DialogDescription className="text-[#6E7A74]">
              Así quedaría tu próximo resumen con tus datos actuales. No se envía ningún correo.
            </DialogDescription>
          </DialogHeader>
          {preview === "loading" && (
            <div className="py-10 flex flex-col items-center gap-3" data-testid="preview-loading">
              <div className="w-10 h-10 rounded-full border-4 border-[#E5DFD5] border-t-[#3A6B56] animate-spin" />
              <p className="text-sm text-[#6E7A74]">Preparando tu resumen…</p>
            </div>
          )}
          {preview && preview !== "loading" && preview.error && (
            <p className="py-8 text-center text-base text-[#B91C1C]" data-testid="preview-error">
              No se pudo cargar la vista previa. Inténtalo de nuevo.
            </p>
          )}
          {preview && preview !== "loading" && !preview.error && preview.vacio && (
            <div className="py-10 flex flex-col items-center gap-3 text-center" data-testid="preview-vacio">
              <Icons.CheckCircle2 size={40} className="text-[#3A6B56]" />
              <p className="text-base text-[#1C2421] font-medium">Esta semana no tienes asuntos relevantes.</p>
              <p className="text-sm text-[#6E7A74]">No recibirás ningún correo esta semana.</p>
            </div>
          )}
          {preview && preview !== "loading" && !preview.error && !preview.vacio && (
            <div data-testid="preview-contenido">
              <p className="text-sm text-[#6E7A74] mb-2">
                <strong className="text-[#1C2421]">Asunto:</strong> {preview.asunto}
              </p>
              <iframe
                title="Vista previa del resumen semanal"
                srcDoc={preview.html}
                className="w-full h-[420px] rounded-xl border border-[#E5DFD5] bg-white"
                data-testid="preview-iframe"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const AvisoPrefs = () => {  const { getAvisosProactivos, avisoTipo } = useData();
  const [tipos, setTipos] = useState(null);
  useEffect(() => {
    getAvisosProactivos().then((d) => setTipos(d.tipos)).catch(() => setTipos({}));
    // eslint-disable-next-line
  }, []);
  const toggle = async (id) => {
    const nuevo = !(tipos?.[id] ?? true);
    setTipos((t) => ({ ...t, [id]: nuevo }));
    try {
      await avisoTipo(id, nuevo);
      toast.success(nuevo ? "Aviso activado" : "Aviso desactivado");
    } catch {
      setTipos((t) => ({ ...t, [id]: !nuevo }));
      toast.error("No se pudo actualizar.");
    }
  };
  if (tipos === null) return null;
  return (
    <div className="bg-white border border-[#E5DFD5] rounded-2xl p-6 shadow-sm" data-testid="aviso-prefs">
      <h2 className="font-heading text-lg font-bold text-[#1C2421] flex items-center gap-2">
        <Icons.BellRing size={20} className="text-[#3A6B56]" /> Avisos en Inicio
      </h2>
      <p className="text-sm text-[#6E7A74] mt-1 mb-4">Elige qué avisos proactivos quieres ver en la pantalla de inicio.</p>
      <div className="space-y-2.5">
        {AVISO_TIPOS.map((t) => {
          const on = tipos?.[t.id] ?? true;
          return (
            <button
              key={t.id}
              data-testid={`aviso-pref-${t.id}`}
              onClick={() => toggle(t.id)}
              className="w-full flex items-center justify-between gap-3 min-h-[52px] px-4 rounded-xl bg-[#FAF8F5] border border-[#E5DFD5] hover:border-[#3A6B56] transition-colors"
            >
              <span className="text-base text-[#1C2421]">{t.label}</span>
              <span className={`relative w-12 h-7 rounded-full transition-colors shrink-0 ${on ? "bg-[#3A6B56]" : "bg-[#D6CFC2]"}`}>
                <span className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-all ${on ? "left-6" : "left-1"}`} />
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

